import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { runGated } from "@/lib/adsGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Calendar as CalIcon, Sparkles, Trash2, Plus, Loader2, Droplet, Bell, Shirt } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { syncCalendarNotifications } from "@/lib/native";

const OCCASIONS = ["Office", "Wedding", "Birthday", "Date", "Business Meeting", "Party", "Festival", "Vacation", "Casual"];
const RECURRENCE = [
  { id: "one_time", label: "One time" },
  { id: "daily",    label: "Daily" },
  { id: "weekly",   label: "Weekly" },
  { id: "monthly",  label: "Monthly" },
];

const FILTERS = [
  { id: "all",      label: "All" },
  { id: "outfit",   label: "Outfits" },
  { id: "reminder", label: "Reminders" },
  { id: "beauty",   label: "Beauty" },
];

const EVENT_KINDS = [
  { id: "outfit",   label: "Outfit event",   icon: Shirt, desc: "Stylebay picks a look" },
  { id: "reminder", label: "Custom reminder", icon: Bell,  desc: "Buzz me at the right time" },
];

export default function Calendar() {
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("list");
  const [cursor, setCursor] = useState(new Date());
  const [draft, setDraft] = useState({ kind: "outfit", title: "", event_at: "", occasion: "Office", location: "", notes: "", recurrence: "one_time" });
  const [busyId, setBusyId] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    const { data } = await api.get("/calendar/events");
    setEvents(data);
    // Sync native phone notifications with every calendar event so users
    // get a phone buzz for outfit events + custom reminders. Beauty
    // reminders keep their own dedicated sync from the Routine Planner.
    try {
      await syncCalendarNotifications(data.filter((e) => (e.kind || "outfit") !== "beauty"));
    } catch { /* web / non-native — no-op */ }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!draft.title || !draft.event_at) { toast.error("Title and date required"); return; }
    try {
      const payload = { ...draft };
      // Custom reminders don't need an "occasion" — send a sentinel value
      // so the backend doesn't try to style an outfit for them.
      if (draft.kind === "reminder") payload.occasion = "Reminder";
      const { data } = await api.post("/calendar/events", payload);
      const created = { ...data, kind: data.kind || draft.kind || "outfit" };
      setEvents((e) => [...e, created]);
      setDraft({ kind: "outfit", title: "", event_at: "", occasion: "Office", location: "", notes: "", recurrence: "one_time" });
      setOpen(false);
      // Reschedule notifications so the new one lands on the phone.
      try {
        const nextList = [...events, created].filter((e) => (e.kind || "outfit") !== "beauty");
        await syncCalendarNotifications(nextList);
        if (draft.kind === "reminder") toast.success("Reminder set — we'll ping your phone.");
      } catch { /* no-op */ }
    } catch (e) { toast.error("Could not save event"); }
  };

  const filtered = events.filter(e => filter === "all" ? true : (e.kind || "outfit") === filter);

  const remove = async (id) => {
    await api.delete(`/calendar/events/${id}`);
    const next = events.filter((x) => x.id !== id);
    setEvents(next);
    try {
      await syncCalendarNotifications(next.filter((e) => (e.kind || "outfit") !== "beauty"));
    } catch { /* no-op */ }
  };

  const suggest = async (id) => {
    setBusyId(id);
    try {
      const { data } = await runGated("calendar_suggest", () => api.post(`/calendar/events/${id}/suggest`));
      setEvents((arr) => arr.map((e) => e.id === id ? { ...e, outfit_suggestion: data } : e));
    } catch (e) {
      if (e?.response?.status !== 402) toast.error(e.response?.data?.detail || "Could not suggest outfit");
    } finally { setBusyId(null); }
  };

  // Month grid helpers
  const monthDays = (() => {
    const start = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const end = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
    const offset = start.getDay();
    const days = [];
    for (let i = 0; i < offset; i++) days.push(null);
    for (let d = 1; d <= end.getDate(); d++) days.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
    return days;
  })();

  const eventsForDay = (d) => events.filter((e) => {
    if (!d) return false;
    const kind = e.kind || "outfit";
    if (kind === "beauty") return false; // beauty reminders keep their own timeline (recurring)
    const dt = new Date(e.event_at);
    if (isNaN(dt.getTime())) return false;
    return dt.getFullYear() === d.getFullYear() && dt.getMonth() === d.getMonth() && dt.getDate() === d.getDate();
  });

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-16">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="eyebrow flex items-center gap-2"><CalIcon size={12} /> Calendar · Smart events</p>
          <h1 className="editorial-h1 mt-3">Outfits, scheduled.</h1>
        </div>
        <Button
          data-testid="calendar-new-btn"
          onClick={() => setOpen(!open)}
          className="rounded-none uppercase tracking-[0.2em] text-xs h-11"
        >
          <Plus size={14} className="mr-2" /> Add event
        </Button>
      </div>

      <p className="mt-4 text-xs text-muted-foreground max-w-xl">
        Schedule outfit events, custom reminders, or beauty routines — Stylebay hands you the right look, or a phone buzz when it&apos;s time. Beauty rituals from the <Link to="/beauty/routine" className="underline underline-offset-2">Routine Planner</Link> appear here too.
      </p>

      <div className="mt-6 flex flex-wrap gap-2" data-testid="calendar-filter-chips">
        {FILTERS.map(f => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            data-testid={`calendar-filter-${f.id}`}
            className={`h-9 px-4 text-[11px] uppercase tracking-[0.18em] border ${filter === f.id ? "bg-accent text-accent-foreground border-foreground" : "border-border hover:border-foreground"}`}
          >{f.label}</button>
        ))}
      </div>

      {open && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-8 border border-border p-6 bg-card"
        >
          {/* Event kind — outfit vs pure reminder. Reminder mode hides the
              occasion field and skips the "Style this event" AI action. */}
          <div className="mb-5">
            <Label className="eyebrow">What are you scheduling?</Label>
            <div className="mt-2 grid grid-cols-2 gap-2" data-testid="calendar-kind-picker">
              {EVENT_KINDS.map((k) => {
                const Icon = k.icon;
                const active = draft.kind === k.id;
                return (
                  <button
                    key={k.id}
                    type="button"
                    onClick={() => setDraft({ ...draft, kind: k.id })}
                    data-testid={`calendar-kind-${k.id}`}
                    className={`flex items-start gap-3 p-3 border text-left transition-colors ${
                      active
                        ? "border-foreground bg-accent/10"
                        : "border-border hover:border-foreground"
                    }`}
                  >
                    <Icon size={16} className={active ? "text-accent mt-0.5" : "text-muted-foreground mt-0.5"} />
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.18em]">{k.label}</p>
                      <p className="text-[10px] text-muted-foreground mt-1">{k.desc}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label className="eyebrow">Title</Label>
              <Input
                data-testid="calendar-title-input"
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                placeholder={draft.kind === "reminder" ? "Pick up dry-cleaning" : "Cousin's wedding"}
                className="rounded-none mt-1.5 h-11"
              />
            </div>
            <div>
              <Label className="eyebrow">Date & time</Label>
              <Input
                data-testid="calendar-date-input"
                type="datetime-local"
                value={draft.event_at}
                onChange={(e) => setDraft({ ...draft, event_at: e.target.value })}
                className="rounded-none mt-1.5 h-11"
              />
            </div>
            {draft.kind === "outfit" && (
              <div>
                <Label className="eyebrow">Occasion</Label>
                <Select value={draft.occasion} onValueChange={(v) => setDraft({ ...draft, occasion: v })}>
                  <SelectTrigger className="rounded-none mt-1.5 h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>{OCCASIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="eyebrow">Repeats</Label>
              <Select value={draft.recurrence} onValueChange={(v) => setDraft({ ...draft, recurrence: v })}>
                <SelectTrigger data-testid="calendar-recurrence-select" className="rounded-none mt-1.5 h-11"><SelectValue /></SelectTrigger>
                <SelectContent>{RECURRENCE.map((r) => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {draft.kind === "outfit" && (
              <div>
                <Label className="eyebrow">Location</Label>
                <Input value={draft.location} onChange={(e) => setDraft({ ...draft, location: e.target.value })} className="rounded-none mt-1.5 h-11" />
              </div>
            )}
            <div className={draft.kind === "reminder" ? "md:col-span-2" : ""}>
              <Label className="eyebrow">Notes</Label>
              <Input
                data-testid="calendar-notes-input"
                value={draft.notes}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                placeholder={draft.kind === "reminder" ? "Body of the phone notification" : ""}
                className="rounded-none mt-1.5 h-11"
              />
            </div>
          </div>
          {draft.kind === "reminder" && (
            <p className="mt-4 text-[11px] text-muted-foreground flex items-center gap-2">
              <Bell size={12} className="text-accent" />
              On the app, this reminder will buzz your phone at the scheduled time.
            </p>
          )}
          <Button onClick={create} data-testid="calendar-save-btn" className="mt-6 rounded-none uppercase tracking-[0.2em] text-xs h-11">
            {draft.kind === "reminder" ? "Save reminder" : "Save event"}
          </Button>
        </motion.div>
      )}

      <div className="mt-10 grid gap-4">
        {view === "month" && (
          <div className="border border-border p-6 bg-card" data-testid="calendar-month-grid">
            <div className="flex items-center justify-between mb-6">
              <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
                      className="px-3 py-1 text-xs uppercase tracking-[0.18em] hover:opacity-60">‹ Prev</button>
              <p className="font-serif text-3xl tracking-tight">
                {cursor.toLocaleString("en-US", { month: "long", year: "numeric" })}
              </p>
              <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
                      className="px-3 py-1 text-xs uppercase tracking-[0.18em] hover:opacity-60">Next ›</button>
            </div>
            <div className="grid grid-cols-7 gap-px bg-border">
              {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
                <div key={d} className="bg-background text-[10px] uppercase tracking-[0.2em] p-2 text-center text-muted-foreground">{d}</div>
              ))}
              {monthDays.map((d, i) => {
                const evts = eventsForDay(d);
                const isToday = d && d.toDateString() === new Date().toDateString();
                return (
                  <div key={i} className={`bg-background min-h-[80px] p-2 ${isToday ? "ring-1 ring-accent" : ""}`}>
                    {d && (
                      <>
                        <p className={`text-xs ${isToday ? "font-bold" : "text-muted-foreground"}`}>{d.getDate()}</p>
                        <div className="mt-1 space-y-1">
                          {evts.slice(0, 3).map((e) => (
                            <p key={e.id} className="text-[10px] truncate bg-accent text-accent-foreground px-1 py-0.5"
                               title={`${e.title} · ${e.recurrence || "one_time"}`}>
                              {e.title}
                            </p>
                          ))}
                          {evts.length > 3 && <p className="text-[10px] text-muted-foreground">+{evts.length - 3} more</p>}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {view === "list" && filtered.length === 0 && (
          <div className="border border-dashed border-border p-16 text-center">
            <p className="font-serif text-3xl">Nothing on the calendar.</p>
            <p className="text-sm text-muted-foreground mt-3">
              {filter === "beauty"
                ? (<>Enable reminders in the <Link to="/beauty/routine" className="underline">Routine Planner</Link>.</>)
                : filter === "reminder"
                  ? "Add a custom reminder and we'll ping your phone at the right time."
                  : "Add your next event to get a styled look ready."}
            </p>
          </div>
        )}
        {view === "list" && filtered.map((e) => {
          const kind = e.kind || "outfit";
          const isBeauty = kind === "beauty";
          const isReminder = kind === "reminder";
          const dt = new Date(e.event_at);
          const validDate = !isNaN(dt.getTime());
          return (
          <motion.div
            key={e.id}
            data-testid={`calendar-event-${e.id}`}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className={`border p-6 ${
              isBeauty ? "border-accent/40 bg-accent/5"
                : isReminder ? "border-accent/30 bg-background"
                : "border-border bg-card"
            }`}
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground flex items-center gap-2">
                  {isBeauty && <Droplet size={11} className="text-accent" />}
                  {isReminder && <Bell size={11} className="text-accent" />}
                  {validDate ? dt.toLocaleString() : (e.event_at || "—")}
                </p>
                <p className="font-serif text-3xl tracking-tight mt-1">{e.title}</p>
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mt-2">
                  {isReminder ? "Custom reminder" : e.occasion}
                  {e.location ? ` · ${e.location}` : ""}
                  {e.recurrence && e.recurrence !== "one_time" && <span className="ml-2 px-1.5 py-0.5 bg-accent/10 text-accent rounded">{e.recurrence}</span>}
                </p>
                {e.notes && <p className="text-sm mt-3 text-muted-foreground italic">{e.notes}</p>}
              </div>
              {!isBeauty && !isReminder && (
                <div className="flex gap-2">
                  <Button
                    data-testid={`calendar-suggest-${e.id}`}
                    onClick={() => suggest(e.id)}
                    disabled={busyId === e.id}
                    className="rounded-none uppercase tracking-[0.2em] text-xs h-10"
                  >
                    {busyId === e.id ? <Loader2 size={14} className="animate-spin" /> : <><Sparkles size={12} className="mr-2" /> {e.outfit_suggestion ? "Re-style" : "Style this event"}</>}
                  </Button>
                  <Button variant="outline" onClick={() => remove(e.id)} className="rounded-none h-10 w-10 p-0">
                    <Trash2 size={14} />
                  </Button>
                </div>
              )}
              {isReminder && (
                <Button
                  variant="outline"
                  onClick={() => remove(e.id)}
                  data-testid={`calendar-reminder-remove-${e.id}`}
                  className="rounded-none h-10 w-10 p-0"
                >
                  <Trash2 size={14} />
                </Button>
              )}
              {isBeauty && (
                <Link
                  to="/beauty/routine"
                  className="text-[10px] uppercase tracking-[0.2em] underline underline-offset-4 self-center"
                  data-testid={`calendar-beauty-edit-${e.id}`}
                >Manage</Link>
              )}
            </div>

            {!isBeauty && !isReminder && e.outfit_suggestion && (
              <div className="mt-6 border-t border-border pt-6">
                <p className="eyebrow">Your look</p>
                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Tile label="Top" item={e.outfit_suggestion.top} />
                  <Tile label="Bottom" item={e.outfit_suggestion.bottom} />
                  <Tile label="Shoes" item={e.outfit_suggestion.shoes} />
                  <Tile label="Accessory" item={e.outfit_suggestion.accessories?.[0]} />
                </div>
                {e.outfit_suggestion.reasoning && (
                  <p className="mt-4 font-serif text-xl leading-snug">{e.outfit_suggestion.reasoning}</p>
                )}
                <p className="mt-3 text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Confidence · {e.outfit_suggestion.confidence_score || 0}/100</p>
              </div>
            )}
          </motion.div>
        );})}
      </div>
    </div>
  );
}

function Tile({ label, item }) {
  return (
    <div className="border border-border bg-card">
      <div className="aspect-square bg-muted overflow-hidden">
        {item ? (
          <img src={fileUrl(item.image_path)} alt={item.type} className="h-full w-full object-cover" />
        ) : (
          <div className="h-full flex items-center justify-center text-[10px] uppercase tracking-[0.2em] text-muted-foreground">—</div>
        )}
      </div>
      <p className="p-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</p>
      {item && <p className="px-2 pb-2 text-xs truncate">{item.type}</p>}
    </div>
  );
}
